import React, { useEffect, useState } from 'react';
import { useLingui } from '@lingui/react';
import { useModalState } from '../Dialogs';
import { FileModalDialog } from '../Dialogs/FileModalDialog';
import { getUnresolvablePlaceholders } from '../../Service/restapi/documentService';
import { ToastS } from '../../Service/ToastS';
import { Box, Text, UnstyledButton } from '@mantine/core';
import { IconLoader, IconPaperclip } from '@tabler/icons';
import { useFileSize } from '../../Service/hooks/useFileSize';

const PdfPreview = ({
  file,
  eventId,
  lastModified,
  fileExists,
  previewIcon,
  fileNameWidth = '100%',
  fileNameSize = 'sm',
}) => {
  const { i18n } = useLingui();
  const { show, toggle } = useModalState();
  const [isLoading, setLoading] = useState(false);
  const [unresolvablePlaceholders, setUnresolvablePlaceholders] = useState(undefined);
  const { fileName, fileSize } = file;
  const [size, unit] = useFileSize(fileSize);
  const hasSize = size !== undefined && size !== 0;

  useEffect(() => {
    fetchUnresolvablePlaceholders();
  }, [file, lastModified]);

  function fetchUnresolvablePlaceholders(notification) {
    if (file.entityType === 'documents' || file.entityType === 'contracts' || file.entityType === 'questionnaires') {
      setLoading(true);
      getUnresolvablePlaceholders(file.entityType, file.entityId)
        .then((unresolvablePlaceholders) => {
          setUnresolvablePlaceholders(unresolvablePlaceholders);
          if (notification && unresolvablePlaceholders.length > 0) {
            ToastS.info('placeholder.unsatisfied', i18n._('placeholder.unsatisfied'));
            unresolvablePlaceholders.forEach((placeholder) => {
              const translation = i18n._(placeholder, { 0: 1 });
              ToastS.info(`unresolvable.placeholder-${translation}`, i18n._(`${translation} nicht gesetzt`));
            });
          }
        })
        .catch(() => {
          console.error('could not load, if document has unresolvable placeholders');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }

  function preview() {
    if (
      (file.entityType === 'documents' || file.entityType === 'contracts' || file.entityType === 'questionnaires') &&
      (!unresolvablePlaceholders || unresolvablePlaceholders.length > 0)
    ) {
      fetchUnresolvablePlaceholders(true);
    } else {
      toggle();
    }
  }

  return (
    <>
      <UnstyledButton
        type="button"
        disabled={isLoading}
        title={fileName}
        sx={(theme) => ({
          alignItems: 'center',
          color:
            fileExists === false
              ? theme.white
              : theme.colorScheme === 'dark'
              ? theme.colors.blue[1]
              : theme.colors.blue[4],
          cursor: isLoading ? 'default' : 'pointer',
          display: 'inline-flex',
          flex: '0 1 auto',
          gap: 4,
          maxWidth: '100%',
          minWidth: 0,
          overflow: 'hidden',
          verticalAlign: 'middle',
        })}
        onClick={() => !isLoading && preview()}
      >
        <Box component="span" sx={{ display: 'inline-flex', flex: '0 0 auto' }}>
          {isLoading ? <IconLoader size={16} /> : previewIcon ?? <IconPaperclip size={16} />}
        </Box>
        <Text
          lineClamp={1}
          size={fileNameSize}
          weight={500}
          sx={{
            flex: '0 1 auto',
            width: fileNameWidth,
            maxWidth: '100%',
            minWidth: 0,
            lineHeight: 1.25,
          }}
        >
          {fileName}
        </Text>
        {hasSize && (
          <Text
            span
            color={fileExists === false ? 'white' : 'dimmed'}
            variant="text"
            sx={{
              flex: '0 1 auto',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.25,
              whiteSpace: 'nowrap',
            }}
          >
            {`(${size} ${unit})`}
          </Text>
        )}
      </UnstyledButton>
      <FileModalDialog
        file={file}
        eventId={eventId || (file ? file.eventId : undefined)}
        allowDownload
        allowEmailAttachment
        allowEditing
        toggle={toggle}
        show={show}
      />
    </>
  );
};

export default PdfPreview;
